/**
 * Format a raw date string as "February 4, 2026", for example.
 * An empty input falls back to an em-dash.
 * When the value can't be parsed, it falls back to the raw string.
 *
 * `locale` defaults to `en-US` because most call sites are staff tables that
 * have always read English. Pass the active i18next language wherever the date
 * lands INSIDE a translated sentence — `4 de febrero de 2026` in a Spanish
 * sentence, not `February 4, 2026`. All four of `en`/`es`/`fr`/`pt-br` are
 * accepted verbatim by `Intl` (BCP-47 tags are case-insensitive), so
 * `i18n.resolvedLanguage` can be handed straight through.
 */
export function formatLongDate(
  raw: string | null | undefined,
  locale = 'en-US',
): string {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Largest unit first, so a 90-minute gap reads "1 hour ago", not "90 minutes ago". */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * Format an instant as "3 hours ago" in the caller's language.
 *
 * Formatted here rather than server-side because the backend's `format_date`
 * builds its English strings by hand (its own TODO), while the locale is already
 * on the client and `Intl.RelativeTimeFormat` translates for free.
 */
export function formatRelativeTime(
  raw: string | null | undefined,
  locale: string,
): string {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= unitSeconds) {
      return formatter.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return formatter.format(seconds, 'second');
}
